// src/admin/PluginManagement.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import {
  Puzzle, MessageCircle, Zap, Shield, TrendingUp, Gift,
  Settings, ToggleLeft, ToggleRight, ChevronRight,
  Bell, CreditCard, BarChart3, Star, Smartphone, Truck
} from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';

const PluginManagement = () => {
  const [plugins, setPlugins] = useState({
    whatsapp: null,
    whatsappOrder: null,
    postex: null,
    appDownload: null,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPlugins = async () => {
      try {
        const [waSnap, waOrderSnap, postexSnap, appSnap] = await Promise.all([
          getDoc(doc(db, 'settings', 'whatsapp')),
          getDoc(doc(db, 'settings', 'whatsapp-order')),
          getDoc(doc(db, 'settings', 'postex')),
          getDoc(doc(db, 'settings', 'app-download')),
        ]);

        setPlugins({
          whatsapp: waSnap.exists() ? waSnap.data() : null,
          whatsappOrder: waOrderSnap.exists() ? waOrderSnap.data() : null,
          postex: postexSnap.exists() ? postexSnap.data() : null,
          appDownload: appSnap.exists() ? appSnap.data() : null,
        });
      } catch (error) {
        console.error('Error fetching plugins:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPlugins();
  }, []);

  const pluginList = [
    {
      id: 'postex',
      name: 'PostEx Integration',
      description: 'Book orders on PostEx automatically via API with CN generation',
      icon: Truck,
      iconColor: 'text-orange-600',
      bgColor: 'bg-orange-50',
      path: '/admin/postex',
      enabled: plugins.postex?.active === true,
      activeFeatures: plugins.postex?.active ? ['Auto Booking'] : [],
    },
    {
      id: 'whatsappOrder',
      name: 'WhatsApp Order Confirmation',
      description: 'Automatically send WhatsApp message when customer places an order',
      icon: FaWhatsapp,
      iconColor: 'text-green-600',
      bgColor: 'bg-green-50',
      path: '/admin/whatsapp-order',
      enabled: plugins.whatsappOrder?.orderConfirmationEnabled === true,
      activeFeatures: plugins.whatsappOrder?.orderConfirmationEnabled
        ? ['Auto Message']
        : [],
    },
    {
      id: 'whatsapp',
      name: 'WhatsApp Floating Button',
      description: 'Floating chat button on website for customer support',
      icon: MessageCircle,
      iconColor: 'text-green-600',
      bgColor: 'bg-green-50',
      path: '/admin/whatsapp',
      enabled: plugins.whatsapp?.active !== false,
      activeFeatures: plugins.whatsapp?.active !== false ? ['Floating Button'] : [],
    },
    {
      id: 'appDownload',
      name: 'App Download Page',
      description: 'Mobile app download page with Play Store + App Store links',
      icon: Smartphone,
      iconColor: 'text-purple-600',
      bgColor: 'bg-purple-50',
      path: '/admin/app-download',
      enabled: plugins.appDownload?.active !== false,
      activeFeatures: ['Download Page'],
    },
    {
      id: 'reviews',
      name: 'Product Reviews',
      description: 'Customer reviews with admin approval system',
      icon: Star,
      iconColor: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      path: '/admin/reviews',
      enabled: true,
      activeFeatures: ['Reviews System'],
    },
    {
      id: 'notifications',
      name: 'Push Notifications',
      description: 'Send notifications to customers with popup',
      icon: Bell,
      iconColor: 'text-blue-600',
      bgColor: 'bg-blue-50',
      path: '/admin/notifications',
      enabled: true,
      activeFeatures: ['Notifications'],
    },
    {
      id: 'coupons',
      name: 'Coupon System',
      description: 'Discount coupons with percentage/flat types',
      icon: Gift,
      iconColor: 'text-pink-600',
      bgColor: 'bg-pink-50',
      path: '/admin/coupons',
      enabled: true,
      activeFeatures: ['Coupons'],
    },
    {
      id: 'shipping',
      name: 'Shipping & Tax',
      description: 'Configure shipping methods and tax rates',
      icon: TrendingUp,
      iconColor: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
      path: '/admin/shipping-tax',
      enabled: true,
      activeFeatures: ['Shipping', 'Tax'],
    },
    {
      id: 'payment',
      name: 'Payment Methods',
      description: 'COD, Bank Transfer with QR code',
      icon: CreditCard,
      iconColor: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      path: '/admin/settings',
      enabled: true,
      activeFeatures: ['COD', 'Bank Transfer'],
    },
    {
      id: 'analytics',
      name: 'Analytics Dashboard',
      description: 'Sales, orders, customers statistics',
      icon: BarChart3,
      iconColor: 'text-orange-600',
      bgColor: 'bg-orange-50',
      path: '/admin/dashboard',
      enabled: true,
      activeFeatures: ['Stats'],
    },
  ];

  if (loading) {
    return (
      <div className="text-center py-16">
        <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-pink-400 border-t-transparent"></div>
        <p className="mt-4 text-gray-500">Loading plugins...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-6xl">

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <Puzzle className="text-pink-500" size={28} /> Plugins
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          Website ke saare plugins yahan se manage karein.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-md p-5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-pink-100 flex items-center justify-center">
              <Puzzle size={24} className="text-pink-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-800">{pluginList.length}</p>
              <p className="text-xs text-gray-500">Total Plugins</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-md p-5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
              <ToggleRight size={24} className="text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-800">
                {pluginList.filter(p => p.enabled).length}
              </p>
              <p className="text-xs text-gray-500">Active</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-md p-5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center">
              <ToggleLeft size={24} className="text-gray-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-800">
                {pluginList.filter(p => !p.enabled).length}
              </p>
              <p className="text-xs text-gray-500">Disabled</p>
            </div>
          </div>
        </div>
      </div>

      {/* Plugins Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {pluginList.map(plugin => {
          const Icon = plugin.icon;
          return (
            <Link
              key={plugin.id}
              to={plugin.path}
              className="bg-white rounded-xl shadow-md p-5 hover:shadow-lg transition-all duration-300 group card-lift"
            >
              <div className="flex items-start gap-4">
                <div className={`w-14 h-14 rounded-2xl ${plugin.bgColor} flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform`}>
                  <Icon size={28} className={plugin.iconColor} />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <h3 className="font-bold text-base text-gray-800 truncate">
                      {plugin.name}
                    </h3>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ${
                      plugin.enabled
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-500'
                    }`}>
                      {plugin.enabled ? 'ACTIVE' : 'DISABLED'}
                    </span>
                  </div>

                  <p className="text-xs text-gray-500 mb-2 line-clamp-2">
                    {plugin.description}
                  </p>

                  {plugin.activeFeatures.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {plugin.activeFeatures.map((feat, i) => (
                        <span key={i} className="text-[10px] font-semibold px-2 py-0.5 rounded bg-pink-50 text-pink-600">
                          {feat}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <ChevronRight size={20} className="text-gray-400 group-hover:text-pink-500 group-hover:translate-x-1 transition-all shrink-0" />
              </div>
            </Link>
          );
        })}
      </div>

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <p className="text-xs text-blue-800 leading-relaxed">
          <strong>💡 Tip:</strong> Har plugin ko click karke uske settings kholein. Wahan se A to Z control kar sakte hain — activate, disable, API keys, aur bahut kuch.
        </p>
      </div>

    </div>
  );
};

export default PluginManagement;